import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { KycAndNotesBanner } from '@/components/app/KycAndNotesBanner';
import { KpiCard } from '@/components/app/KpiCard';
import { Card } from '@/components/ui/card';
import { StatusPill } from '@/components/app/StatusPill';
import { formatINR, fmtDate, periodLabel, daysFromNow } from '@/lib/format';
import { IndianRupee, Banknote, AlertTriangle, Building2, Wrench, Calendar, TrendingUp, Activity } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';

export default function OwnerDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/dashboard/owner').then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const k = data.kpi;
  const chartData = (data.chart_demand_vs_collection || []).map((c) => ({
    period: periodLabel(c.period),
    Expected: c.expected,
    Collected: c.collected,
  }));
  const occupancy = data.occupancy_split || { occupied: 0, vacant: 0 };
  const pieData = [
    { name: 'Occupied', value: occupancy.occupied },
    { name: 'Vacant', value: occupancy.vacant },
  ];
  const pieColors = ['hsl(174 72% 34%)', 'hsl(34 92% 45%)'];

  return (
    <div>
      <PageHeader title="Owner dashboard" subtitle="Portfolio performance at a glance" />
      <KycAndNotesBanner pendingKyc={data?.kpi?.pending_kyc_tenants || 0} recentNotes={data?.recent_notes || []} />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
        <KpiCard testId="kpi-card-expected-rent" label="Expected (this month)" value={formatINR(k.expected_rent)} icon={IndianRupee} accent />
        <KpiCard testId="kpi-card-collected-rent" label="Collected" value={formatINR(k.collected_rent)} icon={Banknote} />
        <KpiCard testId="kpi-card-overdue" label="Overdue" value={formatINR(k.overdue_rent)} icon={AlertTriangle} />
        <KpiCard testId="kpi-card-occupancy" label="Occupancy" value={`${k.occupancy_pct}%`} sub={`${k.occupied_units} occupied / ${k.vacant_units} vacant`} icon={Building2} />
        <KpiCard testId="kpi-card-maintenance" label="Maintenance cost" value={formatINR(k.maintenance_cost)} sub={`${k.open_tickets} open tickets`} icon={Wrench} />
        <KpiCard testId="kpi-card-active-leases" label="Active leases" value={k.active_leases} sub={`${k.total_properties} properties`} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mt-6">
        <Card className="lg:col-span-8 p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold">Rent demand vs collection</div>
              <div className="text-xs text-muted-foreground">Last 6 months</div>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="period" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Expected" fill="hsl(206 86% 45%)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Collected" fill="hsl(152 60% 34%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="lg:col-span-4 p-4 md:p-5">
          <div className="text-sm font-semibold">Occupancy split</div>
          <div className="text-xs text-muted-foreground">Across portfolio</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={56} outerRadius={86} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={pieColors[i]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mt-6">
        <Card className="lg:col-span-7 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div className="text-sm font-semibold">Upcoming lease expiries</div>
          </div>
          {data.upcoming_expiries?.length ? (
            <div className="divide-y">
              {data.upcoming_expiries.map((l) => (
                <div key={l.id} className="flex items-center justify-between py-2.5 text-sm">
                  <div>
                    <div className="font-medium">{l.agreement_number}</div>
                    <div className="text-xs text-muted-foreground">Lease ends {fmtDate(l.end_date)}</div>
                  </div>
                  <div className="text-right">
                    <div className="tabular-nums">{formatINR(l.rent_amount)}</div>
                    <div className="text-xs text-muted-foreground">{daysFromNow(l.end_date)} days left</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-8 text-center">No upcoming expiries in the next 90 days</div>
          )}
        </Card>
        <Card className="lg:col-span-5 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <div className="text-sm font-semibold">Recent consultant activity</div>
          </div>
          <div className="space-y-2.5 max-h-[280px] overflow-auto">
            {(data.consultant_activity || []).map((a) => (
              <div key={a.id} className="flex items-start gap-2.5 text-sm">
                <div className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--role-accent))] mt-2 shrink-0" />
                <div className="flex-1">
                  <div className="font-medium">
                    {a.user_name} <span className="text-muted-foreground font-normal">{a.action}</span>{' '}
                    <span className="font-mono text-xs">{a.entity}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{a.detail || ''} · {fmtDate(a.created_at)}</div>
                </div>
              </div>
            ))}
            {!data.consultant_activity?.length && <div className="text-sm text-muted-foreground">No recent activity</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
